import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link, Typography } from '@mui/material';
import { EditOutlined, NorthEast } from '@mui/icons-material';
import { ClearForm } from './actions/ClearForm';
import { TaskActions } from './actions/useTaskActions';
import { dueLabel } from './tasks/deriveTasks';
import { taskHref } from './tasks/links';
import { DESTINATION_TABS } from './tasks/taskActions';
import { ClientTask, DashboardClient } from './tasks/types';
import { DueTag, INK } from './ui';

/** Makes a row's title cover the whole row (its ::after), so the notice is one target; Clear sits above it. */
const stretchedSx = {
  '&::after': { content: '""', position: 'absolute', inset: 0, borderRadius: '8px' },
  '&:focus-visible': { outline: 'none', textDecoration: 'underline' },
  '&:focus-visible::after': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
} as const;

/**
 * One task in the client detail panel. The task itself is the way to do it:
 * work done in another tab of the client's page links there (↗), a check-in
 * opens its form in place (pencil), and other tasks are plain text (the
 * panel's header opens the profile). Managers and admins can clear an
 * alert-backed task from the same line. Today shows the same work grouped
 * (TodayTasks).
 */
export const TaskItem: React.FC<{
  client: DashboardClient;
  task: ClientTask;
  actions: TaskActions;
  now: Date;
  /** Called once this task is cleared and about to leave the list, so the parent can place focus. */
  onCleared?: () => void;
}> = ({ client, task, actions, now, onCleared }) => {
  const [clearing, setClearing] = useState(false);
  const clearButton = useRef<HTMLButtonElement>(null);
  const [returnFocus, setReturnFocus] = useState(false);
  useEffect(() => {
    if (!clearing && returnFocus) {
      clearButton.current?.focus();
      setReturnFocus(false);
    }
  }, [clearing, returnFocus]);
  const canClear = actions.canClear(task);
  const isCheckIn = task.kind === 'checkin' && Boolean(task.checkInType);
  const destination = DESTINATION_TABS[task.kind];
  const interactive = Boolean(destination || isCheckIn);
  const caption = destination ? `opens ${destination}` : isCheckIn ? 'log it here' : null;

  const title = destination ? (
    <Link
      component={RouterLink}
      to={taskHref(client, task)}
      className="task-link"
      aria-label={`${task.title}, opens ${destination}`}
      underline="none"
      sx={{ fontWeight: 600, color: 'primary.dark', ...stretchedSx }}
    >
      {task.title}
      <NorthEast aria-hidden sx={{ fontSize: 14, ml: 0.5, verticalAlign: '-2px' }} />
    </Link>
  ) : isCheckIn ? (
    <Link
      component="button"
      type="button"
      className="task-link"
      aria-label={`${task.title}, opens the check-in form`}
      underline="none"
      onClick={() => actions.openCheckIn(client, task)}
      sx={{ font: 'inherit', fontWeight: 600, color: 'primary.dark', textAlign: 'left', verticalAlign: 'baseline', ...stretchedSx }}
    >
      {task.title}
      <EditOutlined aria-hidden sx={{ fontSize: 14, ml: 0.5, verticalAlign: '-2px' }} />
    </Link>
  ) : (
    task.title
  );

  return (
    <Box
      component="li"
      sx={{
        position: 'relative',
        mx: -1.25,
        px: 1.25,
        py: 1.5,
        borderRadius: '8px',
        '& + &': { borderTop: `1px solid ${INK.faint}` },
        // No hover tint while the clear form is open: the row is then about the form, not the link.
        ...(interactive && !clearing
          ? {
              transition: 'background-color 120ms ease-out',
              '&:hover': { bgcolor: '#F3F6FD' },
              '&:hover .task-link': { textDecoration: 'underline', textUnderlineOffset: '3px' },
            }
          : {}),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: canClear ? 'minmax(0, 1fr) auto auto' : 'minmax(0, 1fr) auto', columnGap: 1.5, alignItems: 'start' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="span" sx={{ display: 'block', fontWeight: 500, fontSize: 14, color: INK.main }}>
            {title}
          </Typography>
          {task.detail || caption ? (
            <Typography sx={{ fontSize: 13, color: INK.muted, mt: 0.25 }}>
              {task.detail}
              {caption ? (
                <Box component="span" aria-hidden>
                  {task.detail ? ' · ' : ''}
                  {caption}
                </Box>
              ) : null}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ pt: '1px' }}>
          <DueTag urgency={task.urgency}>{dueLabel(task, now)}</DueTag>
        </Box>
        {canClear ? (
          <Button
            ref={clearButton}
            size="small"
            variant="outlined"
            aria-label={`Clear "${task.title}"`}
            disabled={clearing}
            onClick={() => setClearing(true)}
            sx={{
              position: 'relative',
              zIndex: 1,
              minWidth: 0,
              height: 28,
              px: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              color: INK.body,
              borderColor: '#D5DCE5',
              borderRadius: '8px',
              bgcolor: '#FFFFFF',
              '&:hover': { borderColor: INK.muted, bgcolor: '#FFFFFF' },
            }}
          >
            Clear
          </Button>
        ) : null}
      </Box>
      {clearing ? (
        <ClearForm
          taskTitle={task.title}
          onCancel={() => {
            setClearing(false);
            setReturnFocus(true);
          }}
          onClear={(reason, note) => {
            setClearing(false);
            actions.clear(client, task, reason, note);
            onCleared?.();
          }}
        />
      ) : null}
    </Box>
  );
};
